import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import Container from "../components/Container";
import SectionTitle from "../components/Home/SectionTitle";
import { useSiteContent } from "../context/SiteContentContext";

const defaultContent = {
  hero: {
    eyebrow: "الأرشيف الصوتي",
    title: "المكتبة الصوتية",
    description: "ملفات صوتية عربية محفوظة من الأرشيف القديم.",
  },
  featuredPrayer: {
    eyebrow: "صلاة",
    title: "صلاة مار الياس الحي",
    description: "صلاة محفوظة في الأرشيف القديم.",
    image: "/media/gallery/slat-mar-elias.jpg",
  },
  sections: {
    prayerAudioTitle: "صوتيات الصلاة",
    collectionsTitle: "مجموعات التراتيل",
    collectionsDescription: "",
    archiveNote: "",
  },
  prayerTracks: [],
  collections: [],
};

function getAudioMimeType(file) {
  const extension = file.split(".").pop()?.toLowerCase();

  if (extension === "wma") {
    return "audio/x-ms-wma";
  }

  return "audio/mpeg";
}

function getFormatLabel(file) {
  const extension = file.split(".").pop()?.toUpperCase();
  return extension || "AUDIO";
}

function isWmaFile(file) {
  return file.toLowerCase().endsWith(".wma");
}

function AudioTrack({ track, index }) {
  return (
    <article className="rounded-[24px] border border-[#eadfce] bg-white p-5 text-right shadow-[0_18px_60px_rgba(76,58,37,0.08)]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[22px] font-bold text-[#2f281f]">{track.title}</h3>
        <span className="rounded-full bg-[#f4ede2] px-4 py-2 text-xs font-bold text-[#7b674d]">
          {track.category ?? `مادة ${index + 1}`}
        </span>
      </div>
      {track.description ? (
        <p className="mt-3 text-[15px] leading-7 text-[#5d5348]">
          {track.description}
        </p>
      ) : null}
      <audio controls preload="none" className="mt-5 w-full">
        <source src={track.file} type={getAudioMimeType(track.file)} />
        متصفحك لا يدعم تشغيل الصوت.
      </audio>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <a
          href={track.file}
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-[#8a7355] hover:text-[#6f5c43]"
        >
          فتح الملف مباشرة
        </a>
        <span className="rounded-full border border-[#d8ccb8] px-3 py-1 text-xs font-bold text-[#6e5c47]">
          {getFormatLabel(track.file)}
        </span>
        {isWmaFile(track.file) ? (
          <span className="text-xs text-[#8a7355]">
            قد تحتاج ملفات WMA إلى الفتح المباشر في بعض المتصفحات.
          </span>
        ) : null}
      </div>
    </article>
  );
}

function CollectionCard({ collection, index }) {
  return (
    <details
      className="group rounded-[30px] border border-[#eadfce] bg-white p-6 text-right shadow-[0_18px_60px_rgba(76,58,37,0.08)] open:bg-[#fbf8f2] md:p-8"
      {...(index === 0 ? { open: true } : {})}
    >
      <summary className="list-none cursor-pointer">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <p className="text-sm font-bold text-[#8a7355]">
              {collection.eyebrow}
            </p>
            <h2 className="mt-3 text-[28px] font-bold text-[#2f281f] md:text-[32px]">
              {collection.title}
            </h2>
            <p className="mt-3 text-[15px] leading-8 text-[#5d5348]">
              {collection.description}
            </p>
            {collection.note ? (
              <p className="mt-4 rounded-[20px] bg-[#f7efe3] px-4 py-3 text-sm leading-7 text-[#6d5b46]">
                {collection.note}
              </p>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            <span className="rounded-full bg-[#f4ede2] px-4 py-2 text-xs font-bold text-[#7b674d]">
              {collection.badge}
            </span>
            <span className="rounded-full border border-[#d8ccb8] px-4 py-2 text-xs font-bold text-[#6e5c47] transition-colors group-open:bg-[#8a7355] group-open:text-white">
              افتح التراتيل
            </span>
          </div>
        </div>
      </summary>

      <div className="mt-6 grid gap-4 border-t border-[#e8ddcc] pt-6">
        {collection.tracks.map((track, trackIndex) => (
          <article
            key={`${collection.title}-${track.title}`}
            className="rounded-[24px] border border-[#eadfce] bg-white p-4"
          >
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#f4ede2] text-sm font-bold text-[#6f5c43]">
                  {trackIndex + 1}
                </span>
                <h3 className="text-[18px] font-bold text-[#2f281f] md:text-[20px]">
                  {track.title}
                </h3>
              </div>
              <span className="rounded-full border border-[#d8ccb8] px-3 py-1 text-xs font-bold text-[#6e5c47]">
                {getFormatLabel(track.file)}
              </span>
            </div>

            {track.description ? (
              <p className="mb-4 text-[14px] leading-7 text-[#5d5348]">
                {track.description}
              </p>
            ) : null}

            <audio controls preload="none" className="w-full">
              <source src={track.file} type={getAudioMimeType(track.file)} />
              متصفحك لا يدعم تشغيل الصوت.
            </audio>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <a
                href={track.file}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#8a7355] hover:text-[#6f5c43]"
              >
                فتح الملف مباشرة
              </a>
              {isWmaFile(track.file) ? (
                <span className="text-xs text-[#8a7355]">
                  قد تحتاج ملفات WMA إلى الفتح المباشر في بعض المتصفحات.
                </span>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </details>
  );
}

export default function AudioLibrary() {
  const [content, setContent] = useState(defaultContent);
  const { tables } = useSiteContent();

  useEffect(() => {
    fetch("/data/audio-library.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`تعذّر تحميل المكتبة الصوتية: ${response.status}`);
        }

        return response.json();
      })
      .then((json) => {
        setContent({
          ...defaultContent,
          ...json,
          hero: {
            ...defaultContent.hero,
            ...json.hero,
          },
          featuredPrayer: {
            ...defaultContent.featuredPrayer,
            ...json.featuredPrayer,
          },
          sections: {
            ...defaultContent.sections,
            ...json.sections,
          },
          prayerTracks: json.prayerTracks ?? defaultContent.prayerTracks,
          collections: json.collections ?? defaultContent.collections,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  return (
    <main className="pb-20">
      <section className="pb-8 pt-10">
        <Container>
          <div className="rounded-[36px] bg-[linear-gradient(135deg,#24201a_0%,#423425_55%,#7e6547_100%)] px-6 py-10 text-right text-white md:px-10 lg:flex lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-bold text-[#d9cab3]">
                {content.hero.eyebrow}
              </p>
              <SectionTitle
                title={content.hero.title}
                classes="mt-3 text-white"
              />
              <p className="mt-4 max-w-2xl text-[16px] leading-7 text-[#f1ebe2]">
                {content.hero.description}
              </p>
            </div>

            <Link
              to="/"
              className="mt-6 inline-flex rounded-full border border-[#d9cab3] px-5 py-3 text-sm font-bold text-[#f3eee5] transition-colors hover:bg-[#d9cab3] hover:text-[#2f281f] lg:mt-0"
            >
              {tables.backHomeLabel}
            </Link>
          </div>
        </Container>
      </section>

      <section>
        <Container>
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
            <article className="overflow-hidden rounded-[32px] border border-[#eadfce] bg-white shadow-[0_18px_60px_rgba(76,58,37,0.08)]">
              <img
                src={content.featuredPrayer.image}
                alt={content.featuredPrayer.title}
                className="h-full min-h-[340px] w-full object-cover"
              />
            </article>

            <article className="rounded-[32px] bg-[#f6f0e6] p-6 text-right shadow-[0_18px_60px_rgba(76,58,37,0.08)] md:p-8">
              <p className="text-sm font-bold text-[#8a7355]">
                {content.featuredPrayer.eyebrow}
              </p>
              <h2 className="mt-4 text-[32px] font-bold text-[#2f281f]">
                {content.featuredPrayer.title}
              </h2>
              <p className="mt-4 text-[16px] leading-8 text-[#5d5348]">
                {content.featuredPrayer.description}
              </p>
              <p className="mt-6 rounded-[24px] bg-white px-5 py-4 text-sm leading-7 text-[#6b5e51]">
                هذه الصورة نُقلت كما كانت محفوظة في الأرشيف القديم، لتبقى
                حاضرة ضمن الذاكرة الروحية للموقع الجديد.
              </p>
            </article>
          </div>
        </Container>
      </section>

      <section className="pt-10">
        <Container>
          <div className="mb-5 text-right">
            <p className="text-sm font-bold text-[#8a7355]">الصلاة</p>
            <SectionTitle
              title={content.sections.prayerAudioTitle}
              classes="mt-2 text-[#3e352b]"
            />
          </div>

          <div className="grid gap-5">
            {content.prayerTracks.map((track, index) => (
              <AudioTrack key={track.title} track={track} index={index} />
            ))}
          </div>
        </Container>
      </section>

      <section className="pt-10">
        <Container>
          <div className="mb-6 text-right">
            <p className="text-sm font-bold text-[#8a7355]">التراتيل</p>
            <SectionTitle
              title={content.sections.collectionsTitle}
              classes="mt-2 text-[#3e352b]"
            />
            <p className="mt-3 max-w-4xl text-[16px] leading-8 text-[#5d5348]">
              {content.sections.collectionsDescription}
            </p>
          </div>

          <div className="grid gap-6">
            {content.collections.map((collection, index) => (
              <CollectionCard
                key={collection.title}
                collection={collection}
                index={index}
              />
            ))}
          </div>

          {content.sections.archiveNote ? (
            <p className="mt-8 rounded-[24px] bg-[#f4ede2] px-5 py-4 text-right text-sm leading-7 text-[#6b5e51]">
              {content.sections.archiveNote}
            </p>
          ) : null}
        </Container>
      </section>
    </main>
  );
}
